import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Search,
  X,
  Save,
  Printer,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import SortFilterTable, { useClientListGrid } from '../ui/SortFilterTable';
import { GridText, StatusPill } from '../ui/gridCell';
import {
  PAYMENT_CLIENT_COLUMNS,
  PAYMENT_ORDER_MODAL_COLUMNS,
  getPaymentClientCellValue,
  getPaymentOrderCellValue,
} from '../ui/tableColumnConfigs';

const PAGE_SIZE = 100;

const CreateReturn = () => {
  const { language } = useLanguage();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadAllMode, setLoadAllMode] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientReturn, setClientReturn] = useState({
    RefNo: '',
    EmployeeId: '',
    Date: new Date().toISOString().split('T')[0],
    TotalOrders: 0,
  });
  const [filters, setFilters] = useState({
    ClientId: '',
    EmirateId: '',
    AreaId: '',
    EmployeeId: '',
  });
  const [selectAll, setSelectAll] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveAndPrint, setSaveAndPrint] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const response = await MainService.GetCompanyProfile();
        setCompanyProfile(response);
      } catch (err) {
        console.error('Error loading company profile:', err);
      }
    };
    loadCompanyProfile();
  }, []);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [employeesResp, emiratesResp, areasResp] = await Promise.all([
          MainService.EmployeesInDropDown(),
          MainService.EmiratesInDropDown(),
          MainService.AreasInDropDown(),
        ]);
        setEmployees(employeesResp || []);
        setEmirates(emiratesResp || []);
        setAreas(areasResp || []);
      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };
    loadDropdowns();
  }, []);

  const loadNextReturnNo = async () => {
    try {
      const response = await MainService.GetNextClientReturnNo();
      if (response) {
        setClientReturn((prev) => ({ ...prev, RefNo: response }));
      }
    } catch (err) {
      console.error('Error loading next return number:', err);
    }
  };

  useEffect(() => {
    loadNextReturnNo();
  }, []);

  const recalcTotalOrders = (clients) =>
    clients.reduce((sum, client) => sum + (client.ReturnCount || 0), 0);

  const getFilterRequestData = () => ({
    EmirateId: filters.EmirateId || 0,
    AreaId: filters.AreaId || 0,
    ClientId: filters.ClientId || 0,
    EmployeeId: filters.EmployeeId || 0,
  });

  const loadClients = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      setLoadAllMode(false);
      setSelectAll(false);

      const response = await MainService.ClientsInClientReturnList({
        ...getFilterRequestData(),
        Skip: (page - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });

      const data = response?.Data || [];
      setAllClients(data);
      setTotalCount(response?.Total || data.length);
      setCurrentPage(page);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
      setAllClients([]);
      setTotalCount(0);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const loadAllClients = async () => {
    try {
      setLoading(true);
      setError('');
      setSelectAll(false);

      let allClientsData = [];
      let skip = 0;
      let total = 0;
      let hasMore = true;
      const baseRequestData = getFilterRequestData();

      while (hasMore) {
        const response = await MainService.ClientsInClientReturnList({
          ...baseRequestData,
          Skip: skip,
          Take: PAGE_SIZE,
        });

        const batch = response?.Data || [];
        total = response?.Total || 0;
        allClientsData = [...allClientsData, ...batch];
        skip += batch.length;

        if (batch.length === 0 || allClientsData.length >= total) {
          hasMore = false;
        }
      }

      setAllClients(allClientsData);
      setTotalCount(total || allClientsData.length);
      setCurrentPage(1);
      setLoadAllMode(true);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } catch (err) {
      console.error('Error loading all clients:', err);
      setError('Failed to load all clients');
      setAllClients([]);
      setTotalCount(0);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const filteredClients = useMemo(() => {
    if (!searchKeyword.trim()) return allClients;
    const keyword = searchKeyword.toLowerCase();
    return allClients.filter(
      (client) =>
        (client.Name || '').toLowerCase().includes(keyword) ||
        String(client.ClientNo ?? '').toLowerCase().includes(keyword) ||
        (client.MobileNo1 || '').toLowerCase().includes(keyword) ||
        (client.LocationName || '').toLowerCase().includes(keyword) ||
        (client.EmirateName || '').toLowerCase().includes(keyword) ||
        (client.AreaName || '').toLowerCase().includes(keyword)
    );
  }, [allClients, searchKeyword]);

  const handleFilterClients = () => {
    if (filters.EmployeeId) {
      setClientReturn((prev) => ({ ...prev, EmployeeId: filters.EmployeeId }));
    }
    loadClients(1);
  };

  const handleRefresh = async () => {
    setFilters({ ClientId: '', EmirateId: '', AreaId: '', EmployeeId: '' });
    setSearchKeyword('');
    setSelectAll(false);
    setClientReturn({
      RefNo: '',
      EmployeeId: '',
      Date: new Date().toISOString().split('T')[0],
      TotalOrders: 0,
    });
    await loadNextReturnNo();
    loadClients(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage || loading || loadAllMode) return;
    loadClients(page);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const filteredIds = new Set(filteredClients.map((c) => c.Id));
    const updatedClients = allClients.map((client) => {
      if (!filteredIds.has(client.Id)) return client;
      if (newSelectAll) {
        const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: true }));
        return {
          ...client,
          checked: true,
          Orders: updatedOrders,
          ReturnCount: updatedOrders.length,
        };
      }
      const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: false }));
      return {
        ...client,
        checked: false,
        Orders: updatedOrders,
        ReturnCount: 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));
  };

  const handleToggleClient = (clientId) => {
    const updatedClients = allClients.map((client) => {
      if (client.Id !== clientId) return client;
      const newChecked = !client.checked;
      if (newChecked) {
        const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: true }));
        return {
          ...client,
          checked: true,
          Orders: updatedOrders,
          ReturnCount: updatedOrders.length,
        };
      }
      const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: false }));
      return {
        ...client,
        checked: false,
        Orders: updatedOrders,
        ReturnCount: 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));
  };

  const handleToggleOrder = (orderId) => {
    if (!selectedClient) return;

    const updatedClients = allClients.map((client) => {
      if (client.Id !== selectedClient.Id) return client;
      const updatedOrders = (client.Orders || []).map((order) => {
        if (order.Id === orderId) {
          return { ...order, IsChecked: !order.IsChecked };
        }
        return order;
      });
      const returnCount = updatedOrders.filter((o) => o.IsChecked).length;
      return {
        ...client,
        Orders: updatedOrders,
        ReturnCount: returnCount,
        checked: returnCount > 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));

    const updatedClient = updatedClients.find((c) => c.Id === selectedClient.Id);
    if (updatedClient) setSelectedClient(updatedClient);
  };

  const handleViewOrders = (client) => {
    setSelectedClient(client);
    setShowOrdersModal(true);
  };

  const handleOpenSaveModal = (shouldPrint = false) => {
    const hasSelected = allClients.some((c) => (c.ReturnCount || 0) > 0);
    if (!hasSelected) {
      setError('Please select orders for return');
      return;
    }
    setSaveAndPrint(shouldPrint);
    setShowSaveModal(true);
  };

  const handleSaveReturn = async () => {
    if (!clientReturn.EmployeeId) {
      setError('Select Employee');
      return;
    }
    if (clientReturn.TotalOrders === 0) {
      setError('Please select orders for return');
      return;
    }

    try {
      setLoading(true);
      const currentDate = new Date(clientReturn.Date);
      const returnData = {
        RefNo: clientReturn.RefNo,
        EmployeeId: clientReturn.EmployeeId,
        Date: clientReturn.Date,
        TotalOrders: clientReturn.TotalOrders,
        Month: currentDate.getMonth() + 1,
        Day: currentDate.getDate(),
        Year: currentDate.getFullYear(),
        ClientReturnClients: allClients
          .filter((c) => (c.ReturnCount || 0) > 0)
          .map((client) => ({
            TotalOrders: client.ReturnCount,
            ClientId: client.Id,
            ClientReturnClientOrders: (client.Orders || [])
              .filter((order) => order.IsChecked)
              .map((order) => ({ OrderId: order.Id })),
          })),
      };

      const response = await MainService.SaveClientReturn(returnData);
      if (response?.Status === 'Success') {
        setSuccess('Return saved successfully');
        setShowSaveModal(false);
        if (saveAndPrint && response.ClientReturnId) {
          await handlePrintReturn(response.ClientReturnId);
        }
        handleRefresh();
      } else {
        setError(response?.Status || 'Failed to save return');
      }
    } catch (err) {
      console.error('Error saving return:', err);
      setError('Failed to save return');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReturn = async (returnId) => {
    try {
      const response = await MainService.PrintClientReturn(returnId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing return:', err);
      setError('Failed to print return');
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const filteredOrders = useMemo(() => {
    if (!selectedClient) return [];
    if (!voucherSearch.trim()) return selectedClient.Orders || [];
    const keyword = voucherSearch.toLowerCase();
    return (selectedClient.Orders || []).filter((order) =>
      (order.VoucherNo || '').toLowerCase().includes(keyword)
    );
  }, [selectedClient, voucherSearch]);

  const selectedOrderCount = useMemo(() => {
    if (!selectedClient) return 0;
    return (selectedClient.Orders || []).filter((o) => o.IsChecked).length;
  }, [selectedClient]);

  const clientColumns = useMemo(() => PAYMENT_CLIENT_COLUMNS, []);
  const orderModalColumns = useMemo(() => PAYMENT_ORDER_MODAL_COLUMNS, []);

  const getClientCell = useCallback((client, field) => getPaymentClientCellValue(client, field), []);
  const getOrderCell = useCallback(
    (order, field) => getPaymentOrderCellValue(order, field, formatCurrency),
    []
  );

  const clientGrid = useClientListGrid({
    columns: clientColumns,
    rows: filteredClients,
    getCellValue: getClientCell,
  });

  const orderGrid = useClientListGrid({
    columns: orderModalColumns,
    rows: filteredOrders,
    getCellValue: getOrderCell,
  });

  const renderClientCell = useCallback((client, column) => {
    switch (column.field) {
      case 'payMode':
        return <GridText value="-" />;
      case 'totalAmount':
        return <GridText value={`AED ${formatCurrency(client.TotalClientMoney)}`} />;
      case 'totalPaying':
        return <GridText value={client.ReturnCount || 0} className="font-semibold text-gray-900" />;
      case 'actions':
        return (
          <button
            type="button"
            onClick={() => handleViewOrders(client)}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-700"
          >
            Orders
          </button>
        );
      default:
        return <GridText value={getPaymentClientCellValue(client, column.field)} />;
    }
  }, []);

  const renderOrderCell = useCallback((order, column) => {
    if (column.field === 'status') {
      return <StatusPill status={order.Status} />;
    }
    if (['productCharge', 'serviceCharge', 'vat', 'clientMoney'].includes(column.field)) {
      const raw = getPaymentOrderCellValue(order, column.field, formatCurrency);
      return <GridText value={raw === '-' ? raw : `AED ${raw}`} />;
    }
    return <GridText value={getPaymentOrderCellValue(order, column.field, formatCurrency)} />;
  }, []);

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              <span>{getPageHeading(language, 'createReturn')}</span>
              <span className="inline-block ms-1" dir="ltr">
                {' '}
                — {clientReturn.RefNo || getPageHeading(language, 'newRef')}
              </span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create client return from return-approved orders</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenSaveModal(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save
            </button>
            <button
              onClick={() => handleOpenSaveModal(true)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Save & Print
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Emirate</label>
            <select
              value={filters.EmirateId}
              onChange={(e) => setFilters((prev) => ({ ...prev, EmirateId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {emirates.map((emirate) => (
                <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
            <select
              value={filters.AreaId}
              onChange={(e) => setFilters((prev) => ({ ...prev, AreaId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {areas.map((area) => (
                <option key={area.Id} value={area.Id}>{area.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Responsible By</label>
            <select
              value={filters.EmployeeId}
              onChange={(e) => setFilters((prev) => ({ ...prev, EmployeeId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {employees.map((employee) => (
                <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex items-end gap-2">
            <button onClick={handleFilterClients} disabled={loading} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              Filter
            </button>
            <button onClick={handleRefresh} disabled={loading} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
              Clear
            </button>
          </div>
          <div className="col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchKeyword && (
                <button onClick={() => setSearchKeyword('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SortFilterTable
          columns={clientColumns}
          grid={clientGrid}
          rows={filteredClients}
          getCellValue={getClientCell}
          renderCell={renderClientCell}
          loading={loading}
          loadingText="Loading clients..."
          emptyText="No clients found"
          minWidth="1500px"
          maxHeight={600}
          showPagination
          leadingHeader={
            <th className="w-[3%] px-4 py-2 text-left">
              <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="h-4 w-4" />
            </th>
          }
          renderLeadingCell={(client) => (
            <td className="px-4 py-2">
              <input
                type="checkbox"
                checked={client.checked || false}
                onChange={() => handleToggleClient(client.Id)}
                className="h-4 w-4"
              />
            </td>
          )}
        />
        {totalCount > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {loadAllMode ? (
                <>Showing all <span className="font-semibold">{allClients.length}</span> of <span className="font-semibold">{totalCount}</span> clients</>
              ) : (
                <>
                  Showing{' '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + (filteredClients.length ? 1 : 0)}</span>
                  {' - '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + filteredClients.length}</span>
                  {' of '}
                  <span className="font-semibold">{totalCount}</span> clients
                </>
              )}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {!loadAllMode && totalCount > PAGE_SIZE && (
                <>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={loading || currentPage <= 1}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </button>
                  <span className="text-sm text-gray-700 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={loading || currentPage >= totalPages}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              {!loadAllMode && totalCount > allClients.length && (
                <button
                  onClick={loadAllClients}
                  disabled={loading}
                  className="px-4 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 text-sm font-medium"
                >
                  Load All ({totalCount})
                </button>
              )}
              {loadAllMode && totalCount > PAGE_SIZE && (
                <button
                  onClick={() => loadClients(1)}
                  disabled={loading}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
                >
                  Use Pagination
                </button>
              )}
            </div>
          </div>
        )}

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-center">
            <p className="text-lg font-semibold text-gray-900">
              Total Orders: <span className="text-primary">{clientReturn.TotalOrders}</span>
            </p>
          </div>
        </div>
      </div>

      {showSaveModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Save Return</h3>
              <button onClick={() => { setShowSaveModal(false); setSaveAndPrint(false); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <select
                  value={clientReturn.EmployeeId}
                  onChange={(e) => setClientReturn((prev) => ({ ...prev, EmployeeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={clientReturn.Date}
                  onChange={(e) => setClientReturn((prev) => ({ ...prev, Date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => { setShowSaveModal(false); setSaveAndPrint(false); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={handleSaveReturn}
                disabled={loading || !clientReturn.EmployeeId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
              >
                {saveAndPrint ? <><Printer className="w-4 h-4" /> Save & Print</> : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showOrdersModal && selectedClient && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Orders — {selectedClient.Name}</h3>
              <button onClick={() => { setShowOrdersModal(false); setSelectedClient(null); setVoucherSearch(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Number</label>
              <input
                type="text"
                value={voucherSearch}
                onChange={(e) => setVoucherSearch(e.target.value)}
                placeholder="Search by voucher number..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <SortFilterTable
              columns={orderModalColumns}
              grid={orderGrid}
              rows={filteredOrders}
              getCellValue={getOrderCell}
              renderCell={renderOrderCell}
              emptyText="No orders found"
              minWidth="1600px"
              maxHeight={500}
              showPagination
              leadingHeader={
                !companyProfile?.IsBlockByOrderSelection ? (
                  <th className="w-[3%] px-4 py-2 text-left text-xs font-medium uppercase text-gray-700">Select</th>
                ) : null
              }
              renderLeadingCell={
                !companyProfile?.IsBlockByOrderSelection
                  ? (order) => (
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={order.IsChecked || false}
                          onChange={() => handleToggleOrder(order.Id)}
                          className="h-4 w-4"
                        />
                      </td>
                    )
                  : null
              }
            />
            <div className="mt-4 flex justify-end">
              <div className="px-4 py-2 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-700">Selected Count: </span>
                <span className="text-sm font-semibold text-gray-900">{selectedOrderCount}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setShowOrdersModal(false); setSelectedClient(null); setVoucherSearch(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreateReturn;
